import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneData, cloneDataOrNull, isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { TimeZone } from '../../../time/timezone.js';
import { Price } from '../price.js';
import { LocalizedString } from '../localized-string.js';
import { OpenAccessProtocol } from './open-access-protocol.js';
import { VersionRange } from '../../core/version-range.js';
import { PaymentMethod } from '../payment-method.js';
import { OpenAccessPassVariationDetails } from './open-access-pass-variation-details.js';
import { OpenAccessPass, OpenAccessPassSource, OpenAccessPassStatus } from './open-access-pass.js';
import { DiscountCodeBase } from '../../../contracts/discount-code.js';

type OpenAccessPassDetailsCreateBaseArgs = {
  status: OpenAccessPassStatus,
  uniqueId: string,
  businessRef: BusinessModuleRef,
  creationDate: dayjs.Dayjs,
  code: string,
  templateId: string,
  originalName: LocalizedString,
  overrideName: LocalizedString | null,
  price: Price,
  originalPrice: Price | null,
  protocol: OpenAccessProtocol,
  variation: OpenAccessPassVariationDetails | null,
  serialNumber: string | null,
  paymentMethod: PaymentMethod | null,
  source: OpenAccessPassSource,
};

export class OpenAccessPassDetails extends DataModel implements DiscountCodeBase {
  static readonly TYPE_ID: string = 'bd5bfe2ea880413e88ee2677901f3b78';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly status: OpenAccessPassStatus,
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly code: string,
    public readonly templateId: string,
    // Name of the template.
    public readonly originalName: LocalizedString,
    // Name the pass was created with.
    public readonly overrideName: LocalizedString | null,
    public readonly price: Price,
    public readonly originalPrice: Price | null,
    public readonly protocol: OpenAccessProtocol,
    public readonly variation: OpenAccessPassVariationDetails | null,
    public readonly serialNumber: string | null,
    public readonly paymentMethod: PaymentMethod | null,
    public readonly source: OpenAccessPassSource,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OpenAccessPassDetails.SCHEMA_VERSION);
  }

  static create(args: OpenAccessPassDetailsCreateBaseArgs): OpenAccessPassDetails {
    return new OpenAccessPassDetails(
        args.status,
        args.uniqueId,
        args.businessRef,
        args.creationDate,
        args.code,
        args.templateId,
        args.originalName,
        args.overrideName,
        args.price,
        args.originalPrice,
        args.protocol,
        args.variation,
        args.serialNumber,
        args.paymentMethod,
        args.source,
    );
  }

  static fromPass(pass: OpenAccessPass): OpenAccessPassDetails {
    return OpenAccessPassDetails.create({
      status: pass.status,
      uniqueId: pass.uniqueId,
      businessRef: pass.businessRef.clone(),
      creationDate: TimeZone.cloneDate(pass.creationDate),
      code: pass.code,
      templateId: pass.templateId,
      originalName: pass.originalName.clone(),
      overrideName: pass.overrideName?.clone() ?? null,
      price: pass.price.clone(),
      originalPrice: pass.originalPrice?.clone() ?? null,
      protocol: cloneData(pass.protocol),
      variation: cloneDataOrNull(pass.variation),
      serialNumber: pass.serialNumber,
      paymentMethod: pass.paymentMethod,
      source: pass.source,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassDetails {
    return OpenAccessPassDetails.create({
      status: toEnumFromString(MapUtils.get<string>(map, '$status'), OpenAccessPassStatus),
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      code: MapUtils.getString(map, 'code'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      templateId: MapUtils.getString(map, 'templateId'),
      originalName: context.fromData<LocalizedString>(MapUtils.getRecord(map, 'originalName')),
      overrideName: context.fromDataOrNull<LocalizedString>(MapUtils.getRecordOrNull(map, 'overrideName')),
      price: Price.decode(MapUtils.getString(map, 'price')),
      originalPrice: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'originalPrice')),
      protocol: context.fromData<OpenAccessProtocol>(MapUtils.getRecord(map, 'protocol')),
      variation: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'variation')),
      serialNumber: MapUtils.getStringOrNull(map, 'serialNumber'),
      paymentMethod: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'paymentMethod'), PaymentMethod),
      source: toEnumFromString(MapUtils.getString(map, 'source'), OpenAccessPassSource),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      '$status': fromEnumAsString(this.status),
      'uniqueId': this.uniqueId,
      'code': this.code,
      'scanCode': this.code,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'templateId': this.templateId,
      'originalName': context.toData(this.originalName),
      'overrideName': context.toDataOrNull(this.overrideName),
      'price': this.price.encode(),
      'originalPrice': this.originalPrice?.encode() ?? null,
      'protocol': context.toData(this.protocol),
      'protocolType': fromEnumAsString(this.protocol.type),
      'variation': context.toDataOrNull(this.variation),
      'variationId': this.variation?.uniqueId ?? null,
      'serialNumber': this.serialNumber,
      'paymentMethod': this.paymentMethod,
      'source': fromEnumAsString(this.source),
    };
  }

  clone(): this {
    return OpenAccessPassDetails.create({
      status: this.status,
      uniqueId: this.uniqueId,
      code: this.code,
      businessRef: this.businessRef.clone(),
      creationDate: TimeZone.cloneDate(this.creationDate),
      templateId: this.templateId,
      originalName: this.originalName.clone(),
      overrideName: this.overrideName?.clone() ?? null,
      price: this.price.clone(),
      originalPrice: this.originalPrice?.clone() ?? null,
      protocol: cloneData(this.protocol),
      variation: cloneDataOrNull(this.variation),
      serialNumber: this.serialNumber,
      paymentMethod: this.paymentMethod,
      source: this.source,
    }) as this;
  }

  isSame(other: OpenAccessPassDetails): boolean {
    if(other === this) {
      return true;
    }

    return other.status === this.status &&
        other.uniqueId === this.uniqueId &&
        other.code === this.code &&
        isSameData(other.businessRef, this.businessRef) &&
        other.templateId === this.templateId &&
        isSameData(other.originalName, this.originalName) &&
        isSameData(other.overrideName, this.overrideName) &&
        isSameData(other.price, this.price) &&
        isSameData(other.originalPrice, this.originalPrice) &&
        isSameData(other.protocol, this.protocol) &&
        isSameData(other.variation, this.variation) &&
        TimeZone.isSameInstant(other.creationDate, this.creationDate) &&
        other.serialNumber === this.serialNumber &&
        other.paymentMethod === this.paymentMethod &&
        other.source === this.source;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPassDetails;
  }

  static assertType(dm: DataModel): dm is OpenAccessPassDetails {
    return dm instanceof OpenAccessPassDetails;
  }
}

export class OpenAccessPassDetailsBuilder extends DataModelBuilder<OpenAccessPassDetails> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassDetails {
    return OpenAccessPassDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', OpenAccessPassStatus, OpenAccessPassStatus.Live);

    if(versionRange.contains(OpenAccessPassDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenAccessPassDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the status field was introduced
    if(version < 2) {
      // Theoretically it's safe to migrate them as [LIVE].
      // Pass details are only stored in an order when applied as a discount and
      // if it was successfully applied then it must have been live at the moment of serialization.
      map['$status'] = fromEnumAsString(OpenAccessPassStatus.Live);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
